import React from "react";
import {
  Grid,
  TextField,
  MenuItem,
  Box,
  Typography,
  FormControlLabel,
  Checkbox,
  CircularProgress,
} from "@mui/material";
import { CreateMeetingFormData } from "./types";
import { useVideoPlatformApi } from "./hooks/useVideoPlatformApi";
import { MeetingCategoryOption } from "../../api/video_platform";

const MEETING_TYPE_INFO = {
  Instant: {
    label: "Instant",
    description:
      "Meeting starts immediately and ends when all participants leave",
    fields: ["title", "description", "hostId"],
  },
  Persistent: {
    label: "Persistent",
    description:
      "Long-running meeting that stays open until manually ended (optional duration limit)",
    fields: ["title", "description", "hostId", "category", "maxDurationHours"],
  },
};

export interface CreateMeetingFormProps {
  formData: CreateMeetingFormData;
  onChange: (data: CreateMeetingFormData) => void;
  enableMaxDuration: boolean;
  onEnableMaxDurationChange: (enabled: boolean) => void;
}

export const CreateMeetingForm: React.FC<CreateMeetingFormProps> = ({
  formData,
  onChange,
  enableMaxDuration,
  onEnableMaxDurationChange,
}) => {
  const { getCategories } = useVideoPlatformApi();

  const [categories, setCategories] = React.useState<MeetingCategoryOption[]>(
    [],
  );
  const [isLoadingCategories, setIsLoadingCategories] = React.useState(false);

  // Load categories when type changes to Persistent
  React.useEffect(() => {
    if (formData.type === "Persistent" && categories.length === 0) {
      setIsLoadingCategories(true);
      getCategories()
        .then((response) => {
          setCategories(response.categories);
        })
        .catch((error) => {
          console.error("Failed to load categories:", error);
        })
        .finally(() => {
          setIsLoadingCategories(false);
        });
    }
  }, [formData.type]);

  const handleChange = (field: keyof CreateMeetingFormData, value: string) => {
    onChange({ ...formData, [field]: value });
  };

  const currentTypeInfo =
    MEETING_TYPE_INFO[formData.type as keyof typeof MEETING_TYPE_INFO];
  const shouldShowField = (field: string) =>
    currentTypeInfo?.fields.includes(field) ?? false;

  return (
    <Grid container spacing={2}>
      <Grid size={{ xs: 12 }}>
        <TextField
          fullWidth
          select
          label="Meeting Type"
          value={formData.type}
          onChange={(e) => handleChange("type", e.target.value)}
        >
          {Object.entries(MEETING_TYPE_INFO).map(([type, info]) => (
            <MenuItem key={type} value={type}>
              {info.label}
            </MenuItem>
          ))}
        </TextField>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ mt: 0.5, display: "block" }}
        >
          {currentTypeInfo?.description}
        </Typography>
      </Grid>

      {shouldShowField("title") && (
        <Grid size={{ xs: 12 }}>
          <TextField
            fullWidth
            required
            label="Meeting Title"
            value={formData.title}
            onChange={(e) => handleChange("title", e.target.value)}
            placeholder="Enter meeting title"
          />
        </Grid>
      )}

      {shouldShowField("description") && (
        <Grid size={{ xs: 12 }}>
          <TextField
            fullWidth
            label="Description (Optional)"
            value={formData.description}
            onChange={(e) => handleChange("description", e.target.value)}
            placeholder="Enter meeting description"
            multiline
            rows={2}
          />
        </Grid>
      )}

      {shouldShowField("hostId") && (
        <Grid size={{ xs: 12 }}>
          <TextField
            fullWidth
            label="Host ID (Optional)"
            value={formData.hostId}
            onChange={(e) => handleChange("hostId", e.target.value)}
            placeholder="Leave empty for auto-assignment"
          />
        </Grid>
      )}

      {shouldShowField("category") && (
        <Grid size={{ xs: 12 }}>
          <TextField
            fullWidth
            select
            label="Category (Optional)"
            value={formData.category}
            onChange={(e) => handleChange("category", e.target.value)}
            disabled={isLoadingCategories}
            InputProps={{
              endAdornment: isLoadingCategories ? (
                <CircularProgress size={20} sx={{ mr: 2 }} />
              ) : null,
            }}
          >
            <MenuItem value="">
              <em>None</em>
            </MenuItem>
            {categories.map((cat) => (
              <MenuItem key={cat.id} value={cat.id}>
                {cat.title}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
      )}

      <Grid size={{ xs: 12 }}>
        <FormControlLabel
          control={
            <Checkbox
              checked={formData.isPublicVisible ?? false}
              onChange={(e) =>
                onChange({ ...formData, isPublicVisible: e.target.checked })
              }
            />
          }
          label="Publicly Visible"
        />
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", ml: 4 }}
        >
          If unchecked, only Admin and Hosts can see this meeting
        </Typography>
      </Grid>

      {shouldShowField("maxDurationHours") && (
        <>
          <Grid size={{ xs: 12 }}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={enableMaxDuration}
                  onChange={(e) => onEnableMaxDurationChange(e.target.checked)}
                />
              }
              label="Set maximum duration"
            />
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", ml: 4 }}
            >
              If unchecked, the meeting will have no time limit
            </Typography>
          </Grid>
          {enableMaxDuration && (
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                type="number"
                label="Max Duration (hours)"
                value={formData.maxDurationHours}
                onChange={(e) =>
                  handleChange("maxDurationHours", e.target.value)
                }
                inputProps={{ min: 1, max: 168 }}
              />
            </Grid>
          )}
        </>
      )}
    </Grid>
  );
};

// Helper function to check if form is valid
export const isCreateMeetingFormValid = (
  formData: CreateMeetingFormData,
): boolean => {
  if (!formData.title.trim()) return false;
  return true;
};

// Helper function to get default form data
export const getDefaultCreateMeetingFormData = (): CreateMeetingFormData => ({
  title: "",
  description: "",
  type: "Instant",
  hostId: "",
  scheduledStartTime: "",
  scheduledEndTime: "",
  maxDurationHours: "24",
  earlyJoinMinutes: "15",
  isPublicVisible: false,
  category: "",
});

// Helper function to prepare form data for submission
export const prepareCreateMeetingSubmitData = (
  formData: CreateMeetingFormData,
  enableMaxDuration: boolean,
): CreateMeetingFormData => {
  const submitData = { ...formData };
  if (formData.type === "Persistent" && !enableMaxDuration) {
    submitData.maxDurationHours = "";
  }
  return submitData;
};
